import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AbstractEntityTableView } from '@connect-services/client/components/entity-table/abstract-entity-table-view';
import { baseColumns, TableEditMode } from '@connect-services/client/components/entity-table/entity-table.component';
import { EntityTableCellComponent } from '@connect-services/client/components/entity-table-cell/entity-table-cell.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import {
    Alignment,
    PaginationFooterTemplateComponent,
} from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { NavigationStateService } from '@connect-services/client/guards/reload.guard';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    EditAction,
    InformationSchemaTableExtended,
    LayerAttribute,
    LayerWithAttributes,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { AttributeValidationService } from '@connect-services/client/services/attribute-validation.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import {
    CreatedEntitiesByLayer,
    CreatedEntityTableRow,
    EntityCreatedTableService,
} from '@connect-services/client/services/entity-created-table.service';
import {
    CreationContextParams,
    EntityDetailCreationContextService,
} from '@connect-services/client/services/entity-detail-creation-context.service';
import { HomeService } from '@connect-services/client/services/home.service';
import {
    SelectedEntitiesByLayerId,
    SelectedEntity,
} from '@connect-services/client/services/map/selection/selection-state.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { TableEditModeService } from '@connect-services/client/services/table-edit-mode.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';
import { OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

export const MAIN_COLUMN_KEY = 'businessIdentifier';

const createdEntitiesPageSize = 20;

const defaultSort: SortState = {
    order: OrderEnum.ASC,
    orderBy: MAIN_COLUMN_KEY,
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CheckboxComponent,
        CustomTableComponent,
        EntityTableCellComponent,
        NoDataToDisplayComponent,
        PaginationFooterTemplateComponent,
        RessourcesModule,
        TabComponent,
        TabContentDirective,
    ],
    selector: 'app-entity-created-table',
    styleUrl: './entity-created-table.component.scss',
    templateUrl: './entity-created-table.component.html',
})
export class EntityCreatedTableComponent extends AbstractEntityTableView {
    public override readonly onlySelected = input.required<boolean>();
    public readonly switchCreatedEntitiesTable = output<void>();
    public readonly toggleOnlySelectedEntities = output<void>();

    protected readonly Alignment = Alignment;
    protected readonly ButtonType = ButtonType;
    protected readonly currentLayerEntities = computed<CreatedEntityTableRow[]>(() => {
        const createdEntitiesByLayer: CreatedEntitiesByLayer[] =
            this.entityCreatedTableService.createdEntitiesByLayer();

        return createdEntitiesByLayer[this.currentTabIndex()]?.entities ?? [];
    });
    protected readonly currentLayerWithAttributes = computed<LayerWithAttributes | null>(() => {
        const currentLayer: CreatedEntitiesByLayer | null =
            this.entityCreatedTableService.createdEntitiesByLayer()[this.currentTabIndex()] ?? null;

        return this.getCurrentLayerWithAttributes(currentLayer);
    });
    protected readonly currentPage = signal(0);
    protected readonly currentSort = signal<SortState>(defaultSort);
    protected readonly currentTabIndex = signal(0);
    protected readonly filteredCurrentLayerEntities = computed<CreatedEntityTableRow[]>(() => {
        return this.filterEntitiesBySearch(this.currentLayerEntities(), this.searchValue());
    });
    protected readonly forceResetSearchBar = signal(false);
    protected readonly headerCheckboxState = computed<CheckboxState>(() => {
        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();
        const entities = this.filteredCurrentLayerEntities();

        if (!this.selectionService.isSelectionActive() || !activeLayers?.length || !entities?.length) {
            return CheckboxState.UNCHECKED;
        }

        return this.getHeaderCheckboxState(activeLayers[0].id, entities.length);
    });
    protected readonly homeService = inject(HomeService);
    protected readonly isEditSessionActive = computed<boolean>(() => {
        if (this.homeService.isExternalPage()) {
            return this.tableEditModeService.isExternalTableEditActive();
        }

        return this.projectEditModeService.isEditModeActive();
    });
    protected readonly isInCreationState: boolean = true;
    protected readonly isInlineTableEditActive = computed<boolean>(() => {
        if (this.tableEditModeService.currentMode() !== TableEditMode.TABLE_EDIT) {
            return false;
        }

        if (this.homeService.isExternalPage()) {
            return this.tableEditModeService.isExternalTableEditActive();
        }

        return true;
    });
    protected override readonly isSelectableCheckbox = computed<boolean>(() => {
        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();

        return !!activeLayers?.length && this.selectionService.isSelectionActive();
    });
    protected readonly isSelectedLayerHasEntities = computed<boolean>(() => {
        const requestedLayerName = this.getRequestedLayerName();

        if (!requestedLayerName) {
            return true;
        }

        return this.entityCreatedTableService.createdEntitiesByLayer().some((layer: CreatedEntitiesByLayer) => {
            return layer.layerName === requestedLayerName;
        });
    });
    protected readonly paginatedCurrentLayerEntities = computed<CreatedEntityTableRow[]>(() => {
        const endIndex = (this.currentPage() + 1) * createdEntitiesPageSize;
        const startIndex = this.currentPage() * createdEntitiesPageSize;

        return this.sortedCurrentLayerEntities().slice(startIndex, endIndex);
    });
    protected readonly paginationParameters = computed((): PaginationParameters => {
        const order: OrderEnum = this.currentSort().order ?? OrderEnum.ASC;
        const orderBy: string = this.currentSort().orderBy ?? MAIN_COLUMN_KEY;
        const totalItems: number = this.filteredCurrentLayerEntities().length;

        return {
            order,
            orderBy,
            page: this.currentPage(),
            take: createdEntitiesPageSize,
            totalItems,
        };
    });
    protected readonly searchValue = signal<string>('');
    protected readonly sortedCurrentLayerEntities = computed<CreatedEntityTableRow[]>(() => {
        return this.sortEntitiesByColumn(this.filteredCurrentLayerEntities(), this.currentSort());
    });
    protected readonly tableColumns = computed<CustomColumn[]>((): CustomColumn[] => {
        const currentLayerWithAttributes: LayerWithAttributes | null = this.currentLayerWithAttributes();

        return this.buildTableColumns(currentLayerWithAttributes);
    });
    protected readonly tabs = computed<TabWithState[]>((): TabWithState[] => {
        return this.entityCreatedTableService
            .createdEntitiesByLayer()
            .map((layer: CreatedEntitiesByLayer): TabWithState => {
                return {
                    active: true,
                    canCreate: false,
                    createDisabled: true,
                    createTooltipKey: '',
                    name: layer.layerName,
                };
            })
            .filter((tab: TabWithState) => {
                if (!this.onlySelected()) {
                    return true;
                }

                const layer = this.projectEntityTableService.getLayerByName(tab.name) ?? null;

                if (!layer) {
                    return false;
                }

                const selectedByLayerId: Record<string, SelectedEntity[]> =
                    this.selectionStateService.selectedEntitiesByLayerId();

                const selectedEntities: SelectedEntity[] = selectedByLayerId[layer.id] ?? [];

                return !!selectedEntities.length;
            });
    });

    private readonly areAllSelected = computed<boolean>(() => {
        if (!this.selectionService.isSelectionActive()) {
            return false;
        }

        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();
        const totalItems = this.currentLayerEntities().length;

        if (!activeLayers?.length || !totalItems) {
            return false;
        }

        const selectedByLayerId: Record<string, SelectedEntity[]> =
            this.selectionStateService.selectedEntitiesByLayerId();

        const selectedCount =
            selectedByLayerId[activeLayers[0].id]?.filter((entity: SelectedEntity) => entity.isInCreationState)
                .length ?? 0;

        return selectedCount >= totalItems;
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly attributeValidationService = inject(AttributeValidationService);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly entityCreatedTableService = inject(EntityCreatedTableService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    private readonly navigationStateService = inject(NavigationStateService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly router = inject(Router);
    private readonly tableEditModeService = inject(TableEditModeService);

    public constructor() {
        super();

        effect(() => {
            const createdEntitiesByLayer: CreatedEntitiesByLayer[] =
                this.entityCreatedTableService.createdEntitiesByLayer();

            const currentIndex: number = untracked(() => {
                return this.currentTabIndex();
            });

            if (!createdEntitiesByLayer.length) {
                if (currentIndex !== 0) {
                    this.currentTabIndex.set(0);
                }

                return;
            }

            if (currentIndex > createdEntitiesByLayer.length - 1) {
                this.currentTabIndex.set(0);
            }
        });

        effect(() => {
            const createdEntitiesByLayer = this.entityCreatedTableService.createdEntitiesByLayer();

            untracked(() => this.setCurrentTabIndexWithRequestedLayer(createdEntitiesByLayer));
        });

        effect(() => {
            this.ensureCurrentPageIsValid(this.filteredCurrentLayerEntities());
        });
    }

    protected changeTab(index: number): void {
        this.currentPage.set(0);
        this.currentSort.set(defaultSort);
        this.searchValue.set('');
        this.forceResetSearchBar.set(true);

        this.projectEntityTableService.setActiveLayerByName(
            this.entityCreatedTableService.createdEntitiesByLayer()[index].layerName,
        );
        this.currentTabIndex.set(index);
    }

    protected getAttributeInformation(columnKey: string): InformationSchemaTableExtended | null {
        return this.attributeInformationContext.getAttributeInformation(columnKey);
    }

    protected getProjectId(): string | undefined {
        return this.projectBloc.value()?.id;
    }

    protected handlePageChange(page: number): void {
        this.currentPage.set(page);
    }

    protected handleSearch(searchValue: string): void {
        this.currentPage.set(0);
        this.searchValue.set(searchValue);
    }

    protected hasNumericType(columnKey: string): boolean {
        if (!this.getAttributeInformation(columnKey)) {
            return false;
        }

        return this.attributeValidationService.isNumericType(this.getAttributeInformation(columnKey));
    }

    protected onSort(sort: SortState | undefined): void {
        if (!sort) {
            this.currentSort.set(defaultSort);

            return;
        }

        this.currentSort.set(sort);
    }

    protected async openDetailEntityPanel(entity: CreatedEntityTableRow): Promise<void> {
        if (this.isInlineTableEditActive()) {
            return;
        }

        const projectId = this.projectBloc.value()?.id;
        const context = this.getEntityDetails(entity);
        if (!projectId || !context) {
            return;
        }

        const entityStateContext = context.layerIds
            .map((layerId: string) => ({
                entityState: this.editModeStateService.getEntityState(projectId, layerId, entity.uuid),
                layerId,
            }))
            .find(({ entityState }) => !!entityState);

        if (!entityStateContext?.entityState) {
            return;
        }

        const { entityState, layerId } = entityStateContext;

        const layer = this.projectEntityTableService
            .layers()
            .find((currentLayer: LayerWithAttributes) => currentLayer.id === layerId);

        const depositId = entityState.depositId ?? layer?.deposit;
        if (!depositId) {
            return;
        }

        if (entityState.action !== EditAction.CREATE) {
            return this.projectEntityTableService.toggleEntityDetailPanel(depositId, context.entityId);
        }

        const currentCreatedEntityId = this.entityDetailCreationContextService.entityId();

        const isSwitchingBetweenCreatedEntities =
            this.entityDetailCreationContextService.isEditingExistingCreatedEntity() &&
            !!currentCreatedEntityId &&
            currentCreatedEntityId !== context.entityId;

        const isLayerEditable = this.projectEditModeService.editableLayerIds().includes(layerId);

        this.entityDetailCreationContextService.initEditingExistingCreatedEntity(context, isLayerEditable);

        if (!isSwitchingBetweenCreatedEntities) {
            this.navigationStateService.markNavigated();
            await this.navigateToCreatedEntityDetail(projectId, context, layerId, depositId);

            return;
        }

        this.projectEntityTableService.currentPanelEntity.set(context.entityId);
        this.projectEntityTableService.currentPanelContext.set({
            depositId,
            entityId: context.entityId,
        });
    }

    protected toggleAllSelected(): void {
        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();

        if (!activeLayers?.length) {
            return;
        }

        const layerId: string = activeLayers[0].id;

        if (this.areAllSelected()) {
            this.unselectAll(layerId);

            return;
        }

        this.selectAll(layerId);
    }

    private buildTableColumns(currentLayerWithAttributes: LayerWithAttributes | null): CustomColumn[] {
        if (!currentLayerWithAttributes) {
            return [];
        }

        const bookmarkedAttributesColumns: CustomColumn[] = currentLayerWithAttributes.dataAttributes
            .filter((attribute: LayerAttribute) => {
                return attribute.isBookmarked;
            })
            .map((attribute: LayerAttribute) => {
                const column = this.projectEntityTableService.columnsByName()?.get(attribute.column);

                return {
                    key: attribute.column,
                    label: column?.alias ?? attribute.column,
                    sortable: true,
                    tooltip: column?.alias ? column.name : undefined,
                    translate: false,
                };
            });

        return [...baseColumns, ...bookmarkedAttributesColumns];
    }

    private ensureCurrentPageIsValid(entities: CreatedEntityTableRow[]): void {
        const filteredEntitiesCount = entities.length;
        const maxPageIndex =
            filteredEntitiesCount > 0 ? Math.floor((filteredEntitiesCount - 1) / createdEntitiesPageSize) : 0;

        if (this.currentPage() > maxPageIndex) {
            this.currentPage.set(0);
        }
    }

    private filterEntitiesBySearch(entities: CreatedEntityTableRow[], searchValue: string): CreatedEntityTableRow[] {
        let filteredEntities: CreatedEntityTableRow[] = entities;

        const normalizedSearchValue: string = searchValue?.trim().toLowerCase();

        const activeLayerId = this.activeLayerId();

        if (!activeLayerId) {
            return [];
        }

        const selectedByLayerId: Record<string, SelectedEntity[]> =
            this.selectionStateService.selectedEntitiesByLayerId();

        const unSelectedByLayerId: Record<string, SelectedEntity[]> =
            this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId();

        if (this.onlySelected()) {
            filteredEntities = filteredEntities.filter(
                (entity: CreatedEntityTableRow) =>
                    (selectedByLayerId[activeLayerId] ?? []).some(
                        (selectedEntity: SelectedEntity) => selectedEntity.uuid === entity.uuid,
                    ) ||
                    (unSelectedByLayerId[activeLayerId] ?? []).some(
                        (unSelectedEntity: SelectedEntity) => unSelectedEntity.uuid === entity.uuid,
                    ),
            );
        }

        if (!normalizedSearchValue) {
            return filteredEntities;
        }

        return filteredEntities.filter((entity: CreatedEntityTableRow) => {
            const businessIdentifier = entity[MAIN_COLUMN_KEY];

            if (businessIdentifier == null) {
                return false;
            }

            return businessIdentifier.toString().toLowerCase().includes(normalizedSearchValue);
        });
    }

    private getCurrentLayerWithAttributes(currentLayer: CreatedEntitiesByLayer | null): LayerWithAttributes | null {
        if (!currentLayer) {
            return null;
        }

        return (
            this.projectEntityTableService.layers().find((layer: LayerWithAttributes) => {
                return layer.id === currentLayer.layerId;
            }) ?? null
        );
    }

    private getEntityDetails(entity: CreatedEntityTableRow): CreationContextParams | null {
        const currentLayer: LayerWithAttributes | null = this.currentLayerWithAttributes();
        if (!currentLayer) {
            return null;
        }

        const layers: LayerWithAttributes[] =
            this.projectEntityTableService.layersByName()?.get(currentLayer.name) ?? [];

        const layerIds: string[] = layers.map((layer: LayerWithAttributes) => layer.id);
        const depositIds: string[] = [
            ...new Set(
                layers
                    .map((layer: LayerWithAttributes) => layer.deposit)
                    .filter((deposit): deposit is string => !!deposit),
            ),
        ];

        if (!layerIds.length || !depositIds.length) {
            return null;
        }

        return {
            depositIds,
            entityId: entity.uuid,
            layerIds,
        };
    }

    private getRequestedLayerName(): string | null {
        const layerName: string | null = this.activatedRoute.snapshot.queryParamMap.get('layerName');

        if (!layerName) {
            return null;
        }

        return decodeURI(layerName);
    }

    private navigateToCreatedEntityDetail(
        projectId: string,
        context: CreationContextParams,
        layerId: string,
        depositId: string,
    ): Promise<boolean> {
        const navigateTo: string[] = [
            RoutePathEnum.PROJECTS,
            projectId,
            RoutePathEnum.LAYERS,
            layerId,
            RoutePathEnum.ENTITY_DETAIL,
            context.entityId,
            'CREATE',
        ];

        const queryParams: Record<string, string | boolean> = {
            deposit: depositId,
            layer: layerId,
        };

        if (this.homeService.isExternalPage()) {
            queryParams['ext'] = true;
        }

        return this.router.navigate(navigateTo, {
            queryParams,
            queryParamsHandling: 'merge',
        });
    }

    private selectAll(layerId: string): void {
        this.selectionStateService.updateSelectedEntitiesByLayerId((previous: SelectedEntitiesByLayerId) => {
            const newEntities: SelectedEntity[] = this.filteredCurrentLayerEntities().map(
                (entity: CreatedEntityTableRow) => {
                    let styleType: LayerStyleTypeEnum | undefined;
                    try {
                        const geom = entity['geom'];
                        AssertService.assertGeometryType(geom);
                        styleType = this.resolveStyleType(geom);
                    } catch {
                        styleType = undefined;
                    }

                    return {
                        isInCreationState: this.isInCreationState,
                        isVisible: !!styleType && this.mapLayerService.isLayerCurrentlyVisible(layerId),
                        styleType,
                        uuid: entity.uuid,
                    };
                },
            );

            return {
                ...previous,
                [layerId]: newEntities,
            };
        });

        this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId.update(
            (unSelectedEntitiesByLayerId: SelectedEntitiesByLayerId): SelectedEntitiesByLayerId =>
                Object.fromEntries(
                    Object.entries(unSelectedEntitiesByLayerId).filter(
                        ([key]: [string, SelectedEntity[]]) => key !== layerId,
                    ),
                ),
        );
    }

    private setCurrentTabIndexWithRequestedLayer(createdEntitiesByLayer: CreatedEntitiesByLayer[]): void {
        if (!createdEntitiesByLayer.length) {
            this.currentTabIndex.set(0);

            return;
        }

        const requestedLayerIndex = createdEntitiesByLayer.findIndex((layer: CreatedEntitiesByLayer) => {
            return layer.layerId === this.activeLayerId();
        });

        if (requestedLayerIndex < 0) {
            this.currentTabIndex.set(0);

            this.projectEntityTableService.setActiveLayerByName(createdEntitiesByLayer[0].layerName);

            return;
        }

        if (this.currentTabIndex() === requestedLayerIndex) {
            this.projectEntityTableService.setActiveLayerByName(createdEntitiesByLayer[requestedLayerIndex].layerName);

            return;
        }

        this.currentTabIndex.set(requestedLayerIndex);

        this.projectEntityTableService.setActiveLayerByName(createdEntitiesByLayer[requestedLayerIndex].layerName);
    }

    private sortEntities(firstValue: unknown, secondValue: unknown, order: OrderEnum): number {
        if (firstValue === secondValue) {
            return 0;
        }

        if (firstValue === null || firstValue === undefined) {
            return order === OrderEnum.ASC ? -1 : 1;
        }

        if (secondValue === null || secondValue === undefined) {
            return order === OrderEnum.ASC ? 1 : -1;
        }

        if (firstValue < secondValue) {
            return order === OrderEnum.ASC ? -1 : 1;
        }

        return order === OrderEnum.ASC ? 1 : -1;
    }

    private sortEntitiesByColumn(entities: CreatedEntityTableRow[], sort: SortState): CreatedEntityTableRow[] {
        return entities.toSorted((firstEntity: CreatedEntityTableRow, secondEntity: CreatedEntityTableRow): number => {
            const firstValue = firstEntity[sort.orderBy];
            const secondValue = secondEntity[sort.orderBy];

            return this.sortEntities(firstValue, secondValue, sort.order!);
        });
    }

    private unselectAll(layerId: string): void {
        this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId.update((previous) => {
            return {
                ...previous,
                [layerId]: this.selectionStateService.selectedEntitiesByLayerId()[layerId] ?? [],
            };
        });

        this.selectionStateService.updateSelectedEntitiesByLayerId((previous: SelectedEntitiesByLayerId) => {
            return Object.fromEntries(
                Object.entries(previous).filter(([key]: [string, SelectedEntity[]]) => key !== layerId),
            );
        });
    }
}
